import './App.css';
import { BrowserRouter, Link, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import LoginPage from './pages/Login';
import ResetPasswordPage from './pages/ResetPassword';
import AcceptInvitePage from './pages/AcceptInvite';
import RequestPasswordResetPage from './pages/RequestPasswordReset';
import DashboardPage from './pages/Dashboard';
import TutorSessionPage from './pages/TutorSession';
import ExamSessionPage from './pages/ExamSession';
import ExamResultsPage from './pages/ExamResults';
import SessionSummaryPage from './pages/SessionSummary';
import MasteryAnalyticsPage from './pages/MasteryAnalytics';
import AdminUsersPage from './pages/AdminUsers';
import AdminAuditPage from './pages/AdminAudit';
import AdminHealthPage from './pages/AdminHealth';
import QuestionApprovalsPage from './pages/QuestionApprovals';
import TutorStudentsPage from './pages/TutorStudents';
import TutorLinkRequestsPage from './pages/TutorLinkRequests';
import ParentStudentsPage from './pages/ParentStudents';
import PracticePage from './pages/Practice';
import AccessRestrictedPage from './pages/AccessRestricted';
import UserCreationWizardPage from './pages/UserCreationWizard';
import DesignSystemDemo from './pages/DesignSystemDemo';
import BetaHelpPage from './pages/BetaHelp';
import AccountPage from './pages/Account';
import CohortSignupPage from './pages/CohortSignup';
import CohortManagementPage from './pages/CohortManagement';
import NotFoundPage from './pages/NotFound';
import { getStoredUser, hydrateAuthUser, initializeAuthSession, isAuthenticated, logout, parseJsonSafe } from './api/client';
import { getSession } from './api/tutor';
import { getSessionPath, getSessionRuntimePath, SESSION_RUNTIME_EXAM, SESSION_RUNTIME_TUTOR } from './lib/sessionRouting';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import { SessionRuntimeProvider } from './components/SessionRuntimeContext';
import ToastProvider from './components/toast/ToastProvider';
import { registerDevForceLogoutShortcut } from './auth/devLogout';
import { getRoleHomePath, isRoleAllowed } from './auth/roles';
import { isBetaHelpEnabled } from './betaHelp';

function RequireAuth({ children, authHydrated }) {
  const hasToken = isAuthenticated();

  if (hasToken && !authHydrated) {
    return <p>Loading...</p>;
  }

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function RequireRoles({ children, roles, authHydrated }) {
  const hasToken = isAuthenticated();
  const user = getStoredUser();

  if (hasToken && !authHydrated) {
    return <p>Loading...</p>;
  }

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !user.role) {
    return <Navigate to="/access-restricted" replace />;
  }

  if (!isRoleAllowed(user.role, roles)) {
    return <Navigate to="/access-restricted" replace />;
  }

  return children;
}

function RequireRoleGroup({ children, role, authHydrated }) {
  const hasToken = isAuthenticated();
  const user = getStoredUser();

  if (hasToken && !authHydrated) {
    return <p>Loading...</p>;
  }

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !user.role) {
    return <Navigate to="/access-restricted" replace />;
  }

  if (user.role !== role) {
    return <Navigate to={getRoleHomePath(user.role)} replace />;
  }

  return children;
}

function RequireSessionAccess({ children, authHydrated }) {
  const hasToken = isAuthenticated();
  const user = getStoredUser();
  const { sessionId } = useParams();

  if (hasToken && !authHydrated) {
    return <p>Loading...</p>;
  }

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !user.role) {
    return <Navigate to="/access-restricted" replace />;
  }

  if (user.role === 'PARENT' && sessionId) {
    return <Navigate to={`/sessions/${sessionId}/summary`} replace />;
  }

  if (!isRoleAllowed(user.role, ['ADMIN', 'TUTOR', 'STUDENT'])) {
    return <Navigate to="/access-restricted" replace />;
  }

  return children;
}

// The runtime a session is delivered in is a property of the session, not of
// the URL that was navigated to. Without this gate an EXAM session opened at
// /sessions/:id/tutor renders the tutor runtime and exposes tutor-only support
// artifacts inside exam delivery
// (docs/01_product/session_lifecycle.md:257, 585-590).
//
// The guard fetches the session itself because the runtime pages own their own
// loading and cannot hand the mode back up. It renders neither runtime until
// the mode is known: pending shows a loading line, a failed fetch shows a
// retryable notice with a way back to the dashboard rather than guessing.
function SessionRuntimeGate({ children, runtime }) {
  const { sessionId } = useParams();
  const user = getStoredUser();
  const [status, setStatus] = useState('loading');
  const [session, setSession] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setStatus('loading');
    setSession(null);

    const loadSession = async () => {
      try {
        const response = await getSession(sessionId);
        const data = await parseJsonSafe(response);
        if (!isMounted) {
          return;
        }
        if (!response?.ok || !data) {
          setStatus('error');
          return;
        }
        setSession(data);
        setStatus('ready');
      } catch (error) {
        if (isMounted) {
          setStatus('error');
        }
      }
    };

    loadSession();

    return () => {
      isMounted = false;
    };
  }, [sessionId, reloadToken]);

  if (status === 'loading') {
    return <p>Loading session...</p>;
  }

  if (status === 'error') {
    return (
      <div className="page page-shell">
        <p role="alert" className="notice notice-error">
          We could not confirm which kind of session this is, so we have not opened it yet.
        </p>
        <p>
          <button type="button" className="btn btn-primary" onClick={() => setReloadToken((token) => token + 1)}>
            Try again
          </button>
        </p>
        <p>
          <Link to="/dashboard">Back to dashboard</Link>
        </p>
      </div>
    );
  }

  const requestedPath = getSessionRuntimePath(sessionId, runtime);
  const canonicalPath = getSessionPath(user?.role, session);
  if (canonicalPath !== requestedPath) {
    return <Navigate to={canonicalPath} replace />;
  }

  return (
    <SessionRuntimeProvider session={session}>
      {children}
    </SessionRuntimeProvider>
  );
}

function RequireSessionRuntime({ children, authHydrated, runtime }) {
  return (
    <RequireSessionAccess authHydrated={authHydrated}>
      <SessionRuntimeGate runtime={runtime}>{children}</SessionRuntimeGate>
    </RequireSessionAccess>
  );
}

function App() {
  const [authHydrated, setAuthHydrated] = useState(() => !isAuthenticated());

  useEffect(() => {
    return registerDevForceLogoutShortcut((reason) => logout(reason));
  }, []);

  useEffect(() => {
    const hydrate = async () => {
      initializeAuthSession();
      if (!isAuthenticated()) {
        setAuthHydrated(true);
        return;
      }
      try {
        await hydrateAuthUser();
      } finally {
        setAuthHydrated(true);
      }
    };

    hydrate();
  }, []);

  const hasToken = isAuthenticated();

  if (hasToken && !authHydrated) {
    return (
      <div className="app">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <ToastProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="app">
          <Routes>
            <Route
              path="/login"
              element={isAuthenticated() ? <Navigate to="/dashboard" replace /> : <LoginPage />}
            />
            <Route
              path="/reset-password"
              element={isAuthenticated() ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />}
            />
            <Route
              path="/accept-invite"
              element={isAuthenticated() ? <Navigate to="/dashboard" replace /> : <AcceptInvitePage />}
            />
            <Route
              path="/forgot-password"
              element={isAuthenticated() ? <Navigate to="/dashboard" replace /> : <RequestPasswordResetPage />}
            />
            <Route path="/request-invite" element={<Navigate to="/login" replace />} />
            <Route path="/cohort-signup" element={<CohortSignupPage />} />
            <Route
              path="/beta-help"
              element={
                isBetaHelpEnabled()
                  ? isAuthenticated()
                    ? (
                      <RequireAuth authHydrated={authHydrated}>
                        <AuthenticatedLayout>
                          <BetaHelpPage />
                        </AuthenticatedLayout>
                      </RequireAuth>
                    )
                    : <BetaHelpPage />
                  : <Navigate to={isAuthenticated() ? '/dashboard' : '/login'} replace />
              }
            />
            <Route
              path="/account"
              element={
                <RequireAuth authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <AccountPage />
                  </AuthenticatedLayout>
                </RequireAuth>
              }
            />
            <Route
              path="/dashboard"
              element={
                <RequireAuth authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <DashboardPage />
                  </AuthenticatedLayout>
                </RequireAuth>
              }
            />
            <Route
              path="/access-restricted"
              element={
                <RequireAuth authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <AccessRestrictedPage />
                  </AuthenticatedLayout>
                </RequireAuth>
              }
            />
            <Route
              path="/sessions/:sessionId/tutor"
              element={
                <RequireSessionRuntime authHydrated={authHydrated} runtime={SESSION_RUNTIME_TUTOR}>
                  <AuthenticatedLayout>
                    <TutorSessionPage />
                  </AuthenticatedLayout>
                </RequireSessionRuntime>
              }
            />
            <Route
              path="/sessions/:sessionId/exam"
              element={
                <RequireSessionRuntime authHydrated={authHydrated} runtime={SESSION_RUNTIME_EXAM}>
                  <AuthenticatedLayout>
                    <ExamSessionPage />
                  </AuthenticatedLayout>
                </RequireSessionRuntime>
              }
            />
            <Route
              path="/sessions/:sessionId/exam/results"
              element={
                <RequireSessionAccess authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <ExamResultsPage />
                  </AuthenticatedLayout>
                </RequireSessionAccess>
              }
            />
            <Route
              path="/sessions/:sessionId/summary"
              element={
                <RequireAuth authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <SessionSummaryPage />
                  </AuthenticatedLayout>
                </RequireAuth>
              }
            />
            <Route
              path="/analytics/mastery"
              element={
                <RequireRoles roles={['ADMIN']} authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <MasteryAnalyticsPage />
                  </AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequireRoles roles={['ADMIN']} authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <AdminUsersPage />
                  </AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/admin/users/create"
              element={
                <RequireRoles roles={['ADMIN', 'TUTOR']} authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <UserCreationWizardPage />
                  </AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/users/wizard"
              element={
                <RequireRoles roles={['ADMIN', 'TUTOR']} authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <UserCreationWizardPage />
                  </AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <RequireRoles roles={['ADMIN']} authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <AdminAuditPage />
                  </AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/admin/health"
              element={
                <RequireRoles roles={['ADMIN']} authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <AdminHealthPage />
                  </AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/admin/question-approvals"
              element={
                <RequireRoles roles={['ADMIN']} authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <QuestionApprovalsPage />
                  </AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/tutor/students"
              element={
                <RequireRoleGroup role="TUTOR" authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <TutorStudentsPage />
                  </AuthenticatedLayout>
                </RequireRoleGroup>
              }
            />
            <Route
              path="/tutor-links"
              element={
                <RequireRoles roles={['TUTOR', 'STUDENT', 'PARENT']} authHydrated={authHydrated}>
                  <AuthenticatedLayout><TutorLinkRequestsPage /></AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/cohort"
              element={
                <RequireRoles roles={['ADMIN', 'TUTOR']} authHydrated={authHydrated}>
                  <AuthenticatedLayout><CohortManagementPage /></AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route
              path="/parent/students"
              element={
                <RequireRoleGroup role="PARENT" authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <ParentStudentsPage />
                  </AuthenticatedLayout>
                </RequireRoleGroup>
              }
            />
            <Route
              path="/practice"
              element={
                <RequireRoles roles={['STUDENT', 'TUTOR']} authHydrated={authHydrated}>
                  <AuthenticatedLayout>
                    <PracticePage />
                  </AuthenticatedLayout>
                </RequireRoles>
              }
            />
            <Route path="/design-demo" element={<DesignSystemDemo />} />
            <Route
              path="/"
              element={
                isAuthenticated()
                  ? <Navigate to="/dashboard" replace />
                  : <Navigate to="/login" replace />
              }
            />
            <Route
              path="*"
              element={
                isAuthenticated()
                  ? (
                    <AuthenticatedLayout>
                      <NotFoundPage authenticated />
                    </AuthenticatedLayout>
                  )
                  : <NotFoundPage />
              }
            />
          </Routes>
        </div>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
